/* eslint-disable camelcase */
import { Button, Grid, Typography } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import * as yup from "yup";
import { useSelector } from "react-redux";
import { AppState } from "../../../../redux";
import { usersService } from "../../../../api";
import { useDynamicYupPasswordValidations } from "../../../../libs/yup-validations";
import { ChangePasswordRequestData } from "../../../../models/userData";
import CustomPasswordField from "../../../forms/CustomPasswordField";

interface ChangePasswordForm {
  password: string;
  newPassword: string;
  confirmNewPassword: string;
}

const initialValues = {
  newPassword: "",
  password: "",
  confirmNewPassword: "",
} as ChangePasswordForm;

/**
 * A form component allowing the user to change their password.
 *
 * Uses Formik for form state management and dynamic Yup validations based on current security policy.
 *
 * @returns A React element representing the password change form.
 */
const ChangePassword = () => {
  const { t } = useTranslation(["authentication", "snackbars"]);
  const { securitySettings: policy } = useSelector((state: AppState) => state);

  const { YupValidationPasswordRequired, YupValidationConfirmPassword } =
    useDynamicYupPasswordValidations(policy?.securitySettings);

  const validationSchema = yup.object({
    password: YupValidationPasswordRequired,
    newPassword: YupValidationConfirmPassword("confirmNewPassword"),
    confirmNewPassword: YupValidationConfirmPassword("newPassword"),
  });

  /**
   * Handles form submission by sending the change password request.
   *
   * @param values - Form values containing current and new passwords
   */
  const onSubmitHandler = async (values: ChangePasswordForm) => {
    const request = {
      password: values.password,
      new_password: values.newPassword,
      new_password_confirm: values.confirmNewPassword,
    } as ChangePasswordRequestData;

    usersService.changePassword(request).then((result) => {
      if (result.ok) {
        enqueueSnackbar(t("profile.passwordChanged", { ns: "snackbars" }), {
          variant: "success",
        });
      }
    });
  };

  return (
    <Formik
      key={i18n.language}
      onSubmit={onSubmitHandler}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <Form>
        <Grid container spacing={2} sm={12}>
          <Grid item xs={12}>
            <Typography variant="h6">
              {t("forms.tabs.changePassword", { ns: "common" })}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={7}>
            <Field
              as={CustomPasswordField}
              fullWidth
              autoComplete="current-password"
              id="password"
              label={t("password.change.passwordCurrent", {
                ns: "authentication",
              })}
              name="password"
              required
            />
          </Grid>
          <Grid item xs={12} sm={8} md={7}>
            <Field
              as={CustomPasswordField}
              variant="filled"
              fullWidth
              autoComplete="new-password"
              id="new-password"
              label={t("password.change.passwordNew", {
                ns: "authentication",
              })}
              name="newPassword"
              required
            />
          </Grid>
          <Grid item xs={12} sm={8} md={7}>
            <Field
              as={CustomPasswordField}
              fullWidth
              autoComplete="new-password"
              id="confirm-new-password"
              label={t("password.change.passwordNewConfirm", {
                ns: "authentication",
              })}
              name="confirmNewPassword"
              required
            />
          </Grid>
          <Grid item xs={12} sm={8} md={8}>
            <Button
              id="change-password"
              type="submit"
              size="large"
              disabled={false}
              variant="contained"
              color="primary"
            >
              {t("password.change.changeButton", {
                ns: "authentication",
              })}
            </Button>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

export default ChangePassword;
